/**
 * @file Marqueurs d'un client (VIP, blacklisté, no-show, pas d'avis).
 * @module components.customer.CustomerFlags
 *
 * Affiche les marqueurs posés sur un client (VIP, blacklisté, historique de
 * no-show, demande d'avis écartée) sous forme de badges, de manière cohérente
 * dans toute l'application. Distinct du niveau de fidélité (statut calculé) : ce dernier est rendu par
 * CustomerStatusBadge. Les deux concepts sont volontairement séparés ; un parent
 * qui veut afficher les deux les compose côte à côte.
 *
 * Ordre d'affichage : VIP → Blacklisté → No-Show → Pas d'avis. Le marqueur No-Show
 * est masqué si le client est blacklisté (redondance).
 */

import PropTypes from 'prop-types';
import {Stack} from '@mui/material';
import Label from 'packages/common/components/label';
import Iconify from 'packages/common/components/iconify';
import {
  REVIEW_EXCLUSION_BADGE_LABEL,
  REVIEW_EXCLUSION_ICON,
} from 'packages/common/constants/restaurants/reviewRequests';

/**
 * Tailles de badge (hauteur, taille de police, largeur d'icône).
 */
const BADGE_SIZES = {
  small: {height: 18, fontSize: '0.6rem', iconWidth: 10},
  medium: {height: 20, fontSize: '0.65rem', iconWidth: 12},
  large: {height: 24, fontSize: '0.7rem', iconWidth: 14},
};

/**
 * Marqueurs client (VIP, blacklisté, no-show, pas d'avis).
 *
 * @param {Object} props
 * @param {Object} props.customer - Données du client
 * @param {boolean} props.customer.isVip - Client VIP
 * @param {boolean} props.customer.isBlacklisted - Client blacklisté
 * @param {boolean} props.customer.hasNoShow - Historique de no-show
 * @param {boolean} props.customer.excludeFromReview - Aucune demande d'avis pour ce client
 * @param {string} [props.size='medium'] - Taille des badges ('small', 'medium', 'large')
 * @param {number} [props.spacing=0.5] - Espacement entre les badges (unités MUI)
 * @return {React.ReactElement|null} Stack de marqueurs, ou null si aucun
 */
export function CustomerFlags({customer, size = 'medium', spacing = 0.5}) {
  if (!customer) return null;

  const {height, fontSize, iconWidth} = BADGE_SIZES[size] || BADGE_SIZES.medium;

  const hasFlags = customer.isVip || customer.isBlacklisted || customer.hasNoShow || customer.excludeFromReview;
  if (!hasFlags) return null;

  return (
    <Stack direction="row" spacing={spacing} alignItems="center">
      {/* VIP */}
      {customer.isVip && (
        <Label color="warning" variant="soft" sx={{height, fontSize, fontWeight: 600}}>
          <Iconify icon="solar:star-bold" width={iconWidth} sx={{mr: 0.5}} />
          VIP
        </Label>
      )}

      {/* Blacklisté */}
      {customer.isBlacklisted && (
        <Label color="error" variant="soft" sx={{height, fontSize, fontWeight: 600}}>
          <Iconify icon="solar:danger-triangle-bold" width={iconWidth} sx={{mr: 0.5}} />
          Blacklisté
        </Label>
      )}

      {/* No-Show (masqué si blacklisté, redondant) */}
      {customer.hasNoShow && !customer.isBlacklisted && (
        <Label color="warning" variant="soft" sx={{height, fontSize, fontWeight: 600}}>
          <Iconify icon="solar:clock-circle-bold" width={iconWidth} sx={{mr: 0.5}} />
          No-Show
        </Label>
      )}

      {/* Casse d'origine : la mise en capitales de Label donnerait « Pas D'avis » */}
      {customer.excludeFromReview && (
        <Label color="info" variant="soft" sx={{height, fontSize, fontWeight: 600, textTransform: 'none'}}>
          <Iconify icon={REVIEW_EXCLUSION_ICON} width={iconWidth} sx={{mr: 0.5}} />
          {REVIEW_EXCLUSION_BADGE_LABEL}
        </Label>
      )}
    </Stack>
  );
}

CustomerFlags.propTypes = {
  customer: PropTypes.shape({
    isVip: PropTypes.bool,
    isBlacklisted: PropTypes.bool,
    hasNoShow: PropTypes.bool,
    excludeFromReview: PropTypes.bool,
  }),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  spacing: PropTypes.number,
};

export default CustomerFlags;
